import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MSV } from '@enums/MSV.enum';
import { SessionStorageKey } from '@enums/SessionStorageKey.enum';
import { Market } from '@type/Generic/Market.type';
import { getCookie } from '@utils/Navigation/getCookie.utils';
import Swal from 'sweetalert2';

import { EnvironmentService } from './environment.service';

export type MvpVariant = 'default' | 'carrello' | 'carrello-w-fares' | 'carrello-multi-cells';
const DEFAULT_VARIANT: MvpVariant = 'default';
const SEARCH_CACHE_RESET_EVENT = 'cck-search-cache-reset';

export interface MvpMenuItem {
  variant: MvpVariant;
  label: string;
}

export interface MvpMockMenuItem {
  id: string;
  label: string;
  file: string | null;
  type?: string | null;
  discountCode?: string | null;
  halvePricesOnEmptyOccupancy?: boolean;
}

interface MvpMarketConfig {
  scope?: string[];
  items?: Array<Partial<MvpMenuItem> & { version?: unknown }>;
  mocks?: Array<Partial<MvpMockMenuItem>>;
}

@Injectable({
  providedIn: 'root',
})
export class MvpService {
  private readonly _environmentService = inject(EnvironmentService);
  private readonly _router = inject(Router);
  private readonly _lookupVariable = this._environmentService.isProd() ? 'cckMsvPRO' : 'cckMsvPRE';

  private readonly _consentCookieName = 'cckMvpBetaConsent';
  private readonly _cookieAcceptedValue = 'accepted';
  private readonly _cookieRejectedValue = 'rejected';

  public getResultCardVariant(): MvpVariant {
    return this.getBetaResultCardVariant();
  }

  public getBetaResultCardVariant(): MvpVariant {
    if (!this.isBetaTestingEnabled() || !this.isBetaTestingAccepted()) return DEFAULT_VARIANT;

    return this.getSessionSelectedVariant() ?? this.getMenuItems()[0]?.variant ?? DEFAULT_VARIANT;
  }

  public isBetaTestingEnabled(): boolean {
    return this.getMenuItems().length > 0;
  }

  public isBetaTestingAccepted(): boolean {
    return this.getConsent() === this._cookieAcceptedValue;
  }

  public hasMvpFeatureFlagEnabled(): boolean {
    return this.isBetaTestingEnabled();
  }

  public getMenuItems(): MvpMenuItem[] {
    const config = this.getActiveConfig();
    if (!config) return [];

    if (Array.isArray(config.items) && config.items.length) {
      return config.items
        .map((item) => {
          const variant = this.parseVariant(item?.variant ?? item?.version);
          const label = typeof item?.label === 'string' ? item.label.trim() : '';
          if (!variant || !label) return null;
          return {
            variant,
            label,
          };
        })
        .filter((item): item is MvpMenuItem => !!item);
    }

    return [];
  }

  public getMockMenuItems(): MvpMockMenuItem[] {
    const config = this.getActiveConfig();
    if (!config || !Array.isArray(config.mocks)) return [];

    return config.mocks
      .map((item): MvpMockMenuItem | null => {
        const id = typeof item?.id === 'string' ? item.id.trim() : '';
        const label = typeof item?.label === 'string' ? item.label.trim() : '';
        const file = typeof item?.file === 'string' ? item.file.trim() : null;
        if (!id || !label) return null;
        return {
          id,
          label,
          file: file || null,
          type: typeof item?.type === 'string' ? item.type.trim() || null : null,
          discountCode: typeof item?.discountCode === 'string' ? item.discountCode.trim() || null : null,
          halvePricesOnEmptyOccupancy: item?.halvePricesOnEmptyOccupancy === true,
        };
      })
      .filter((item): item is MvpMockMenuItem => !!item);
  }

  public getSelectedMock(): MvpMockMenuItem | null {
    if (!this.isBetaTestingEnabled() || !this.isBetaTestingAccepted()) return null;

    const items = this.getMockMenuItems();
    if (!items.length) return null;

    const selectedId = sessionStorage.getItem(SessionStorageKey.MVP_SELECTED_MOCK);
    return items.find((item) => item.id === selectedId) ?? items[0];
  }

  public getSelectedMockFileName(): string | null {
    return this.getSelectedMock()?.file ?? null;
  }

  public getSelectedMockType(): string | null {
    return this.getSelectedMock()?.type ?? null;
  }

  public getSelectedMockDiscountCode(): string | null {
    return this.getSelectedMock()?.discountCode ?? null;
  }

  public isSelectedMockType(...types: string[]): boolean {
    const selectedType = this.getSelectedMockType();
    return !!selectedType && types.includes(selectedType);
  }

  public isSelectedMockId(...ids: string[]): boolean {
    const selectedId = this.getSelectedMock()?.id;
    return !!selectedId && ids.includes(selectedId);
  }

  public isFareComparisonMockSelected(): boolean {
    return this.isSelectedMockType('up-to-mix', 'starting-from', 'lastminute') || this.isSelectedMockId('uptomix', 'starting-from', 'lastminute');
  }

  public isFreeDrinksMockSelected(): boolean {
    return this.isSelectedMockType('free-drinks', 'free-drinks-half', 'free-drinks-land-half')
      || this.isSelectedMockId('freedrinks', '50pcFreeDrinks', '50pcFreeDrinks50pcLand');
  }

  public isUpToMixDataMockSelected(): boolean {
    return this.isSelectedMockType('up-to-mix', 'starting-from') || this.isSelectedMockId('uptomix', 'starting-from');
  }

  public initBetaProgramUi(): void {
    if (!this.isBetaTestingEnabled()) return;
    if (this.hasRejectedConsent()) return;

    if (this.isBetaTestingAccepted()) {
      this.injectUserMenuWhenAvailable();
      return;
    }

    this.showBetaConsentSwal();
  }

  public setSelectedVariant(variant: MvpVariant): void {
    sessionStorage.setItem(SessionStorageKey.MVP_SELECTED_VARIANT, variant);
    sessionStorage.removeItem(SessionStorageKey.MVP_SELECTED_VERSION);
  }

  public setSelectedMock(id: string): void {
    if (!this.getMockMenuItems().some((item) => item.id === id)) return;
    const currentId = sessionStorage.getItem(SessionStorageKey.MVP_SELECTED_MOCK);
    sessionStorage.setItem(SessionStorageKey.MVP_SELECTED_MOCK, id);
    if (currentId !== id) {
      window.dispatchEvent(new Event(SEARCH_CACHE_RESET_EVENT));
    }
  }

  public stopBetaProgram(): void {
    sessionStorage.removeItem(SessionStorageKey.MVP_SELECTED_VARIANT);
    sessionStorage.removeItem(SessionStorageKey.MVP_SELECTED_VERSION);
    sessionStorage.removeItem(SessionStorageKey.MVP_SELECTED_MOCK);
    window.dispatchEvent(new Event(SEARCH_CACHE_RESET_EVENT));
    this.setConsent(this._cookieRejectedValue);
    void this.refreshCurrentPage();
  }

  public confirmStopBetaProgram(): void {
    Swal.fire({
      title: 'Smettere di partecipare?',
      html: 'Se vorrai partecipare nuovamente al programma di beta testing dovrai prima svuotare i cookie del browser.<br><br>Sei sicuro di voler smettere di partecipare al programma?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Conferma',
      cancelButtonText: 'Annulla',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        this.stopBetaProgram();
      }
    });
  }

  private getActiveConfig(): MvpMarketConfig | null {
    const data = this.getMsvValue(MSV.MVP);
    if (!data) return null;

    const marketEntries = Array.isArray(data) ? data : [data as Record<string, unknown>];
    const agency = window.agencyCode;
    const username = window.userCode;
    const network = this.getNetworkCode();

    for (const entry of marketEntries) {
      const marketEntry = entry as Record<string, unknown>;
      const marketConfig = marketEntry?.[this.marketCode] ?? marketEntry?.['*'];
      const configs = (Array.isArray(marketConfig) ? marketConfig : [marketConfig]) as MvpMarketConfig[];

      for (const config of configs) {
        const scope = Array.isArray(config?.scope) ? config.scope : [];
        const hasScopeMatch = scope.indexOf('*') >= 0
          || scope.indexOf(agency) >= 0
          || scope.indexOf(username) >= 0
          || scope.indexOf(network) >= 0;
        if (hasScopeMatch) return config;
      }
    }

    return null;
  }

  private showBetaConsentSwal(): void {
    Swal.fire({
      title: 'Programma di beta testing',
      html: 'La tua agenzia e stata selezionata per prendere parte al programma di beta testing, al fine di testare nuove interfacce grafiche del sistema.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Accetto',
      cancelButtonText: 'Rifiuto',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        this.setConsent(this._cookieAcceptedValue);
        this.injectUserMenuWhenAvailable();
      } else {
        this.stopBetaProgram();
      }
    });
  }

  private injectUserMenuWhenAvailable(attempt = 0): void {
    const userMenu = document.querySelector('.c-user-menu');
    if (!userMenu) {
      if (attempt < 20) {
        setTimeout(() => this.injectUserMenuWhenAvailable(attempt + 1), 250);
      }
      return;
    }

    this.injectUserMenu(userMenu);
  }

  private injectUserMenu(userMenu: Element): void {
    const existingItem = userMenu.querySelector<HTMLElement>('[data-mvp-beta-menu="true"]');
    if (existingItem) {
      this.renderUserMenu(existingItem);
      return;
    }

    const item = document.createElement('li');
    item.className = 'c-user-menu__mvp-beta';
    item.setAttribute('role', 'menuitem');
    item.setAttribute('data-mvp-beta-menu', 'true');
    this.renderUserMenu(item);

    userMenu.appendChild(item);
  }

  private renderUserMenu(item: HTMLElement): void {
    const selectedVariant = this.getResultCardVariant();
    const selectedLabel = this.getMenuItems().find((menuItem) => menuItem.variant === selectedVariant)?.label || '';
    const selectedMockLabel = this.getSelectedMock()?.label || '';

    item.innerHTML = this.buildUserMenuHtml(selectedVariant, selectedLabel, selectedMockLabel);
    this.bindUserMenuEvents(item);
  }

  private bindUserMenuEvents(item: HTMLElement): void {
    item.querySelectorAll<HTMLAnchorElement>('[data-mvp-variant]').forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        const variant = this.parseVariant(anchor.dataset['mvpVariant']);
        if (!variant) return;
        this.setSelectedVariant(variant);
        this.updateInjectedUserMenu();
        void this.refreshCurrentPage();
      });
    });

    item.querySelector<HTMLAnchorElement>('[data-mvp-stop]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.confirmStopBetaProgram();
    });

    item.querySelectorAll<HTMLAnchorElement>('[data-mvp-mock]').forEach((anchor) => {
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        const mockId = anchor.dataset['mvpMock'];
        if (!mockId) return;
        this.setSelectedMock(mockId);
        this.updateInjectedUserMenu();
        void this.refreshCurrentPage();
      });
    });
  }

  private updateInjectedUserMenu(): void {
    const item = document.querySelector<HTMLElement>('[data-mvp-beta-menu="true"]');
    if (!item) return;

    this.renderUserMenu(item);
  }

  private buildUserMenuHtml(selectedVariant: MvpVariant, selectedLabel: string, selectedMockLabel: string): string {
    const selectedMockId = this.getSelectedMock()?.id || '';
    const menuItems = this.getMenuItems()
      .map((item) => {
        const isSelected = item.variant === selectedVariant;
        return `
        <li role="none">
          <a role="menuitem"
             href="javascript:void(0)"
             class="${isSelected ? 'is-active' : ''}"
             ${isSelected ? 'aria-current="true"' : ''}
             data-cmp-ab="2"
             data-mvp-variant="${this.escapeHtml(item.variant)}">${this.escapeHtml(item.label)}</a>
        </li>
      `;
      })
      .join('');
    const mockMenuItems = this.getMockMenuItems()
      .map((item) => {
        const isSelected = item.id === selectedMockId;
        return `
        <li role="none">
          <a role="menuitem"
             href="javascript:void(0)"
             class="${isSelected ? 'is-active' : ''}"
             ${isSelected ? 'aria-current="true"' : ''}
             data-cmp-ab="2"
             data-mvp-mock="${this.escapeHtml(item.id)}">${this.escapeHtml(item.label)}</a>
        </li>
      `;
      })
      .join('');

    return `
      <div class="c-dropdown">
        <a href="javascript:void(0)" class="c-user-menu__item c-user-menu__item--dropdown text-nowrap is-active" aria-haspopup="true" aria-expanded="false" onclick="cck_comp_toggleMenu(this);">
          <div class="c-user-menu__adv">
            <span class="c-user-menu__mvp-label">&#11088; BETA TESTER</span>
            <span class="o-name c-user-menu__mvp-version">(Profilo ${this.escapeHtml(selectedLabel)})</span>
          </div>
        </a>
        <div class="c-dropdown-menu">
          <ul class="c-user-nav" role="menu" aria-label="Menu MVP">
            ${menuItems}
            <li role="separator" class="c-user-nav__divider"></li>
            <li role="none">
              <a role="menuitem" href="https://forms.office.com/r/1T4RMKTD6C" target="_blank" rel="noopener noreferrer" data-cmp-ab="2">Invia un feedback</a>
            </li>
            <li role="none">
              <a role="menuitem" href="javascript:void(0)" data-cmp-ab="2" data-mvp-stop="true">Smetti di partecipare al programma di beta testing</a>
            </li>
          </ul>
        </div>
      </div>
      <div class="c-dropdown">
        <a href="javascript:void(0)" class="c-user-menu__item c-user-menu__item--dropdown text-nowrap is-active" aria-haspopup="true" aria-expanded="false" onclick="cck_comp_toggleMenu(this);">
          <div class="c-user-menu__adv">
            <span class="c-user-menu__mvp-label">DATA MOCK</span>
            <span class="o-name c-user-menu__mvp-version">(${this.escapeHtml(selectedMockLabel)})</span>
          </div>
        </a>
        <div class="c-dropdown-menu">
          <ul class="c-user-nav" role="menu" aria-label="Menu data mock MVP">
            ${mockMenuItems}
          </ul>
        </div>
      </div>
    `;
  }

  private getSessionSelectedVariant(): MvpVariant | null {
    const variant = this.parseVariant(
      sessionStorage.getItem(SessionStorageKey.MVP_SELECTED_VARIANT)
      ?? sessionStorage.getItem(SessionStorageKey.MVP_SELECTED_VERSION),
    );
    if (!variant) return null;

    return this.getMenuItems().some((item) => item.variant === variant) ? variant : null;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private getConsent(): string {
    return getCookie(this._consentCookieName);
  }

  private setConsent(value: string): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `${this._consentCookieName}=${value}; expires=${expires.toUTCString()}; path=/`;
  }

  private hasRejectedConsent(): boolean {
    return this.getConsent() === this._cookieRejectedValue;
  }

  private parseVariant(value: unknown): MvpVariant | null {
    if (
      value === 'default'
      || value === 'carrello'
      || value === 'carrello-w-fares'
      || value === 'carrello-multi-cells'
    ) {
      return value;
    }

    const legacyVersion = Number(value);
    if (legacyVersion === 26) return 'default';
    if (legacyVersion === 28) return 'carrello';
    if (legacyVersion === 31) return 'carrello-w-fares';

    return null;
  }

  private getMsvValue(key: MSV) {
    return window[this._lookupVariable]?.[key];
  }

  private get marketCode() {
    return window.marketCode as Market;
  }

  private getNetworkCode(): string {
    try {
      const agencyData = JSON.parse(sessionStorage.getItem(SessionStorageKey.AGENCY_DATA));
      return agencyData?.Network?.trim().toUpperCase() || '';
    } catch {
      return '';
    }
  }

  private async refreshCurrentPage(): Promise<void> {
    const browserUrl = window.location.pathname + window.location.search + window.location.hash;
    const routerUrl = this._router.url;
    const currentUrl = /Result\.aspx/i.test(browserUrl) ? browserUrl : routerUrl;

    if (!/Result\.aspx/i.test(currentUrl)) {
      window.location.reload();
      return;
    }

    try {
      const urlTree = this._router.parseUrl(currentUrl);
      urlTree.queryParams = {
        ...urlTree.queryParams,
        mvpRefresh: Date.now().toString(),
      };
      await this._router.navigateByUrl(urlTree);
    } catch {
      window.location.reload();
    }
  }
}
